import { Card } from "@components/cards/card";
import { useModalContext } from "@hooks/contexts/modal-context";
import {
  IRaffleTypeDTO,
  IRaffleTypeTicketConfigDTO,
} from "@hooks/dtos/raffles/IRaffleTypeDTO";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { api } from "@service/api";
import { useEffect, useState } from "react";
import { FaCircleInfo } from "react-icons/fa6";
import { useLocation, useNavigate } from "react-router-dom";
import parse from "html-react-parser";
import { ModalInfoHtml } from "@components/modals/modal-info/indexHtml";
import { ModalRaffleCampaignCode } from "@components/modals/modal-raffle-code";
import { InputCurrency } from "@components/forms/input-currency";
import { ModalRaffleCampaignDescription } from "@components/modals/modal-raffle-description";
import { Input } from "@components/forms/input";
import { Masks } from "@hooks/functions/Masks/Masks";
import {
  RaffleCreateIsValidTicketPrice,
  RaffleCreateIsValidTicketQuantity,
} from "@hooks/functions/Raffle/create";
import { IProfitTypeDTO } from "@hooks/dtos/profit/IProfitTypeDTO";
import { RaffleCreateCampaignProfitChoose } from "@components/raffle-profit/choose";
import { ImageThumbUploader } from "@components/forms/img-thumb";
import { Button } from "@components/buttons/button";
import { ICreateRaffleDefaultDTO } from "@hooks/dtos/raffles/IRaffleDefaultDTO";
import { RichSelect } from "@components/forms/selectWithDescription";
import { IRaffleCategoryDTO } from "@hooks/dtos/raffles/IRaffleCategoryDTO";
import { RaffleCategoryStatusEnum } from "@hooks/enums/raffle";

const masks = new Masks();
export function MyDefaultRaffleNew() {
  // --- Default Functions --- //
  const navigate = useNavigate();
  const location = useLocation();
  const { changeHash } = useModalContext();
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  // --- Raffle Informations --- //
  const [raffle, setRaffle] = useState<{ [x: string]: any }>({
    typeId: 0,
    categories: [] as number[],
    thumb: undefined,
    campaign: undefined,
    name: undefined,
    descriptionShort: undefined,
    description: undefined,
    ticketAmount: 10,
    ticketPrice: 0,
    rewardPrice: 0,
    rewardPriceMarket: 0,
    profit: {} as IProfitTypeDTO | undefined,
  });

  // --- Raffle Informations --- //
  const [raffleType, setRaffleType] = useState<IRaffleTypeDTO>();
  const [raffleTypes, setRaffleTypes] = useState<IRaffleTypeDTO[]>([]);
  const [raffleCategories, setRaffleCategories] = useState<IRaffleCategoryDTO[]>([]);
  const [isLoadingRaffleCategories, setIsLoadingRaffleCategories] = useState(true);
  const [raffleTypeTicketConfig, setRaffleTypeTicketConfig] =
    useState<IRaffleTypeTicketConfigDTO>();
  const [raffleTypeDescriptions, setRaffleTypeDescriptions] = useState<{
    description: string;
    description_short: string;
  }>({
    description: `Selecione uma opção válida...`,
    description_short: `Selecione...`,
  });

  // --- Some Status Control --- //
  const [canCreate, setCanCreate] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<{ [x: string]: boolean }>({
    raffleType: true,
  });

  const loadRaffleCategories = async () => {
    try {
      setIsLoadingRaffleCategories(true);
      const response = await api.get<IRaffleCategoryDTO[]>(
        `/raffle/categories?status=${RaffleCategoryStatusEnum.ACTIVE}`,
      );
      setRaffleCategories(response.data || []);
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message ||
          "Falha ao carregar as categorias da rifa.",
        type: "error",
        theme: "dark",
      });
    } finally {
      setIsLoadingRaffleCategories(false);
    }
  };

  const loadRaffleTypes = async () => {
    try {
      setIsLoading((prev) => ({
        ...prev,
        raffleType: true,
      }));

      const params = `?status=a`;
      const response = await api.get(`/raffle/types${params}`);

      setRaffleTypes(response?.data);

      setIsLoading((prev) => ({
        ...prev,
        raffleType: false,
      }));
    } catch (error: any) {
      setTimeout(() => {
        // goToHome();
      }, 1500);

      pushToastify({
        message:
          error?.response?.data?.message || "Falha ao carregar as informações.",
        type: "error",
        theme: "dark",
      });
    }
  };

  const getRaffleTypeDescriptionShortById = (id: number) => {
    // --- Find the Raffle Type by Id --- //
    const raffleType = raffleTypes.find((raffleType) => raffleType.id === id);

    // --- Create Default Values --- //
    const descriptions: { description: string; description_short: string } = {
      description: `Selecione uma opção válida...`,
      description_short: `Selecione uma opção válida...`,
    };

    // --- Set Raffle Type Descriptions --- //
    if (raffleType) {
      descriptions.description = raffleType.description ?? ``;
      descriptions.description_short = raffleType.description_short ?? ``;

      // --- Update the Raffle Type Info | Config by Selected Type --- //
      setRaffleType(raffleType);

      const config = raffleType?.config;
      if (config) {
        const ticketConfig = config?.config?.ticket;
        setRaffleTypeTicketConfig(ticketConfig);
      }
    }

    // --- Set the State Values --- //
    setRaffleTypeDescriptions(descriptions);
  };

  const handleModalCampaignCodeClose = (campaign?: string) => {
    // --- Change de Campaign Code Input --- //
    setRaffle((prev) => ({
      ...prev,
      campaign,
    }));
    changeHash("");
  };

  const treatModalCampaignCodeOpen = () => {
    if (raffle.raffleType === Number(0)) {
      pushToastify({
        message: "É obrigatório selecionar um Tipo de Campanha para continuar.",
        type: "error",
        theme: "dark",
      });
      return;
    }

    OpenModal("modalCampaignCode");
  };

  const treatModalCampaignDescriptionOpen = () => {
    OpenModal("modalCampaignDescription");
  };

  const handleModalCampaignDescriptionClose = (description?: string) => {
    // --- Change de Campaign Code Input --- //
    setRaffle((prev) => ({
      ...prev,
      description,
    }));
    changeHash("");
  };

  function OpenModal(text: string) {
    if (!text || text === "") return;
    changeHash(text);
  }

  const handleRaffleTypeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRaffle((prev) => ({
      ...prev,
      campaign: undefined,
      typeId: e.target.value,
    }));
  };

  const handleCanCreate = (): boolean => {
    // --- Set Main Variable --- //
    let success: boolean = true;

    // Check each field individually
    if (!raffle?.typeId) success = false;
    else if (!raffle?.campaign) success = false;
    else if (!raffle?.name) success = false;
    else if (!raffle?.descriptionShort) success = false;
    else if (!raffle?.description) success = false;
    else if (!raffle?.ticketAmount || raffle.ticketAmount === 0)
      success = false;
    else if (!raffle?.ticketPrice || raffle.ticketPrice === 0) success = false;
    else if (!raffle?.rewardPrice || raffle.rewardPrice === 0) success = false;
    else if (!raffle?.rewardPriceMarket || raffle.rewardPriceMarket === 0)
      success = false;
    else if (!raffle?.profit?.id) success = false;
    else if (!raffle?.thumb) success = false;

    // --- Change the Control Create --- //
    setCanCreate(success);

    return success;
  };

  const handleCreateRaffle = async () => {
    try {
      // --- Mount the Data to Send --- //
      const data: ICreateRaffleDefaultDTO = {
        typeId: raffle?.typeId,
        categories: raffle?.categories || [],
        campaign: raffle?.campaign,
        name: raffle?.name,
        descriptionShort: raffle?.descriptionShort,
        description: raffle?.description,
        quantity: raffle?.ticketAmount,
        price: raffle?.ticketPrice,
        rewardPrice: raffle?.rewardPrice,
        rewardPriceMarket: raffle?.rewardPriceMarket,
        profitTypeId: raffle?.profit?.id,
        thumb: raffle?.thumb,
      };

      // --- Send the Data to Save --- //
      const response = await api.post(`/customer/raffle/default/create`, data);

      // --- Set the return data --- //
      const retData = response.data;
      console.log({ data, retData });

      pushToastify({
        message: `Registro realizado com sucesso`,
        type: "success",
        theme: "dark",
      });

      setTimeout(() => {
        navigate(`/raffle/default/view?key=${retData?.key}`);
      }, 1500);
    } catch (error: any) {
      // setTimeout(() => {
      //   goToHome();
      // }, 1500);

      pushToastify({
        message:
          error?.response?.data?.message ||
          "Falha ao processar as informações.",
        type: "error",
        theme: "dark",
      });
    }
  };

  useEffect(() => {
    loadRaffleTypes();
    void loadRaffleCategories();
  }, []);

  useEffect(() => {
    getRaffleTypeDescriptionShortById(Number(raffle.typeId));
  }, [raffle.typeId]);

  useEffect(() => {
    handleCanCreate();
  }, [raffle]);

  return (
    <>
      {/* Main Things */}
      <Card className="mb-5">
        <span className="end-0 bg-info text-white p-2 rounded-top rounded-bottom-0 text-center">
          <span className="px-2">Criação de Campanha</span>
        </span>
        <div className="card-header text-center">
          <div className="card-title">Nova Campanha</div>
        </div>
        <div className="card-body">
          <div className="row">
            {/* Thumb */}
            <div className="form-group">
              <label htmlFor="campaignThumb">
                Capa da Campanha (Thumbnail)
              </label>
              <ImageThumbUploader
                onImageChange={(base64) => {
                  setRaffle((prev) => ({
                    ...prev,
                    thumb: base64,
                  }));
                }}
                initialImage={raffle.thumb}
                resizeWidth={800}
                resizeHeight={800}
                maxKbSize={2048}
                convertToWebp={true}
              />
            </div>

            {/* Select to Chooose Ticket Type */}
            <div className="form-group">
              <label htmlFor="campaignType">Tipo de Tickets da Rifa</label>
              <select
                id="campaignType"
                className="form-select"
                aria-label="Tipo de Rifa"
                onChange={handleRaffleTypeSelect}
                value={raffle.raffleType}
              >
                <option key="selkey-0" value="0">
                  Selecione...
                </option>
                {raffleTypes.map((raffleType) => (
                  <option key={`selkey-${raffleType.id}`} value={raffleType.id}>
                    {raffleType.name}
                  </option>
                ))}
              </select>
              <small
                style={{ cursor: "pointer" }}
                onClick={() => OpenModal("modalInfo")}
              >
                {raffleTypeDescriptions.description_short}
                <FaCircleInfo />
              </small>
            </div>

            {/* Campaign Name */}
            <div className="form-group">
              <label htmlFor="name">Nome da Campanha</label>
              <input
                id="name"
                type="text"
                className="form-control"
                value={raffle.name || ""}
                onChange={(e) =>
                  setRaffle((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
              />
            </div>

            {/* Campaign Code */}
            <div className="form-group d-flex flex-column">
              <label htmlFor="campaign">Código da Campanha</label>
              <span
                className={`text-black py-2 rounded border border-2 border-${
                  raffle.campaign ? `success` : `warning`
                }`}
                onClick={treatModalCampaignCodeOpen}
              >
                <span className="ms-3">
                  {raffle.campaign ||
                    `Clique para criar a identificação da Campanha`}
                </span>
              </span>
              <small>
                Este será o código de identificação e URL da sua Campanha
              </small>
            </div>

            {/* Raffle Categories */}
            <div className="form-group">
              <RichSelect
                label="Categorias da Campanha"
                options={raffleCategories.map((category) => ({
                  value: category.id,
                  name: category.name,
                  description: category.description_short,
                }))}
                value={raffle.categories}
                onChange={(value) => {
                  const categories = Array.isArray(value)
                    ? value.map(Number)
                    : [];
                  setRaffle((prev) => ({ ...prev, categories }));
                }}
                placeholder={
                  isLoadingRaffleCategories
                    ? "Carregando categorias..."
                    : "Selecione uma ou mais categorias"
                }
                isDisabled={isLoadingRaffleCategories}
                isMulti
              />
              <small className="text-muted">
                Selecione as categorias que melhor descrevem a sua rifa.
              </small>
            </div>

            {/* Second Step to Creatin' */}
            {raffle.campaign && (
              <>
                {/* Campaign: Short Description */}
                <div className="form-group">
                  <div className="form-floating">
                    <textarea
                      maxLength={150}
                      className={`form-control`}
                      onChange={(e) =>
                        setRaffle((prev) => ({
                          ...prev,
                          descriptionShort: e.target.value,
                        }))
                      }
                      value={raffle.descriptionShort}
                    ></textarea>
                    <label>Curta Descrição da Campanha</label>
                  </div>
                </div>

                {/* Campaign Description */}
                <div className="form-group d-flex flex-column">
                  <label htmlFor="description">
                    Descrição Completa da Campanha
                  </label>
                  <span
                    className={`bg-light text-black py-2 rounded border border-2 border-${
                      raffle.description ? `success` : `warning`
                    } text-center`}
                    onClick={treatModalCampaignDescriptionOpen}
                  >
                    <span className="">
                      {raffle.description
                        ? `Descrição da Campanha registrada com sucesso`
                        : `Clique para adicionar uma descrição à Campanha`}
                    </span>
                  </span>
                  <small>
                    Descrição completa da campanha (aceita formatação
                    HTML/Word).
                  </small>
                </div>

                {/* Tickets */}
                <div className="form-group d-flex gap-3">
                  {/* Amount */}
                  <div className="w-100">
                    <Input
                      label="Quantidade de Tickets (Total)"
                      id={"ticketAmount"}
                      name={"ticketAmount"}
                      defaultValue={raffle.ticketAmount}
                      min={0}
                      mask={"milhar"}
                      onChange={(e) => {
                        setRaffle((prev) => ({
                          ...prev,
                          ticketAmount: Number(
                            masks.formatToNumber(e.target.value)
                          ),
                        }));
                      }}
                      errorMessage={RaffleCreateIsValidTicketQuantity({
                        quantity: raffle.ticketAmount,
                        min: raffleTypeTicketConfig?.quantity?.min,
                        max: raffleTypeTicketConfig?.quantity?.max,
                        type: raffleType?.key,
                      })}
                    />
                  </div>

                  {/* Price */}
                  <div className="w-100">
                    <InputCurrency
                      label="Preço do Ticket (Unidade)"
                      placeholder=""
                      prefix="R$"
                      name="ticketPrice"
                      onChangeTextOut={(v: number, valueFormat?: string) => {
                        setRaffle((prev) => ({
                          ...prev,
                          ticketPrice: v,
                        }));
                      }}
                      min={0}
                      errorMessage={RaffleCreateIsValidTicketPrice({
                        price: raffle.ticketPrice,
                        min: raffleTypeTicketConfig?.price?.min,
                        max: raffleTypeTicketConfig?.price?.max,
                        type: raffleType?.key,
                      })}
                    />
                  </div>

                  {/* Reward(s) Paid Value(s) */}
                  <div className="w-100">
                    <InputCurrency
                      label="Valor Investido no(s) Prêmio(s)"
                      placeholder=""
                      prefix="R$"
                      name="rewardPrice"
                      description="Para cálculo do lucro (não é exibido aos compradores)"
                      onChangeTextOut={(v: number, valueFormat?: string) => {
                        setRaffle((prev) => ({
                          ...prev,
                          rewardPrice: v,
                        }));
                      }}
                      min={0}
                    />
                  </div>

                  {/* Reward(s) Market Value(s) */}
                  <div className="w-100">
                    <InputCurrency
                      label="Valor de Mercado do(s) Prêmio(s)"
                      placeholder=""
                      prefix="R$"
                      name="rewardPriceMarket"
                      description="Total aprox. do(s) produtos(s) (exibe aos usuários)"
                      onChangeTextOut={(v: number, valueFormat?: string) => {
                        setRaffle((prev) => ({
                          ...prev,
                          rewardPriceMarket: v,
                        }));
                      }}
                      min={0}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Campaign Profit Type */}
            <RaffleCreateCampaignProfitChoose
              raffleTypeId={raffle.typeId}
              ticketAmount={raffle.ticketAmount}
              ticketPrice={raffle.ticketPrice}
              rewardPrice={raffle.rewardPrice}
              onChange={(profit) => {
                setRaffle((prev) => ({
                  ...prev,
                  profit,
                }));
              }}
            />

            {/* Campaign Register Button */}
            {raffle?.profit?.id && (
              <div className="col-md-5 d-flex flex-column my-4 mx-auto">
                <Button
                  className={`btn btn-primary mt-2 ${
                    canCreate ? `` : `disabled`
                  }`}
                  onClick={handleCreateRaffle}
                >
                  Criar Campanha
                </Button>
              </div>
            )}
          </div>
        </div>
      </Card>

      <ModalRaffleCampaignCode
        raffleType={raffle.raffleType}
        campaignName={raffle.name}
        isOpen={location?.hash === "#modalCampaignCode"}
        onClose={handleModalCampaignCodeClose}
      />

      <ModalRaffleCampaignDescription
        raffleType={raffle.raffleType}
        campaignName={raffle.name}
        isOpen={location?.hash === "#modalCampaignDescription"}
        onClose={handleModalCampaignDescriptionClose}
      />

      <ModalInfoHtml
        header={`Informações sobre o tipo de rifa`}
        title={`${raffleTypeDescriptions.description_short}`}
        html={parse(raffleTypeDescriptions.description)}
        isOpen={location?.hash === "#modalInfo"}
        onClose={() => {
          changeHash("");
        }}
      />
    </>
  );
}
